"use client";

import { ApiErrorState } from "@/components/error/api-error-state";
import { IHotelsSearchSchema } from "@/feature/hotel/search/types/hotel-search-type";
import { useAppSelector } from "@/redux/hooks";
import { ArrowUp } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Virtuoso } from "react-virtuoso";


import { useHotelSearchQuery } from "../../api/hotel-api";
import { filterHotelResult } from "../../lib/filter-hotel-result";
import { toHotelSearchQueryArgs } from "../../lib/hotel-formatter-helper";
import { useHotelFilter } from "../../state/extract-hotel-filter-slice";
import { HotelFilterSidebar } from "../filters/hotel-filter-sidebar";
import { HotelCardLoadingSkeleton } from "../loader/hotel-card-loading-skeleton";
import HotelListCard from "./hotel-list-card";

type HotelSearchProps = {
  decoded: IHotelsSearchSchema;
};

export default function HotelSearch({ decoded }: HotelSearchProps) {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const { selectedOption } = useAppSelector(useHotelFilter);

  const searchQueryArgs = useMemo(
    () => toHotelSearchQueryArgs(decoded),
    [decoded],
  );
  const { currentData, error, isLoading, isFetching, isError, refetch } =
    useHotelSearchQuery(searchQueryArgs);

  const hotel_list = currentData?.data?.hotels;
  const totalNights = currentData?.data?.no_of_nights;
  const parentSearchId =
    currentData?.data?.parent_search_id ??
    currentData?.data?.parent_search_id;

  const result = useMemo(
    () => filterHotelResult(hotel_list, selectedOption),
    [hotel_list, selectedOption],
  );

  const handleRangeChange = useCallback((range: { startIndex: number }) => {
    const shouldShow = range.startIndex > 4;
    setShowBackToTop((prev) => (prev === shouldShow ? prev : shouldShow));
  }, []);

  // Scroll to top of results when filters change or when data finishes fetching
  const resultsRootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (result.length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [result.length]);

  const renderHotelItem = useCallback(
    (index: number) => {
      const hotel = result[index];
      if (!hotel) return null;

      return (
        <div className="pt-4 ">
          <HotelListCard
            hotel={hotel}
            searchData={decoded}
            no_of_night={totalNights}
            parentSearchId={parentSearchId}
          />
        </div>
      );
    },
    [decoded, parentSearchId, result, totalNights],
  );
  const virtuosoComponents = useMemo(
    () => ({
      Footer: () =>
        result.length > 0 ? (
          <div className="flex justify-center py-4 text-sm text-muted-foreground">
            No more data
          </div>
        ) : null,
    }),
    [result.length],
  );

  if (isError)
    return (
      <ApiErrorState
        error={error}
        title="Error fetching hotel data"
        onRetry={() => void refetch()}
        className="min-h-[50vh]"
      />
    );

  return (
    <section className="container-wrapper">
      <div className="container px-0 relative flex w-full flex-1 flex-col lg:flex-row lg:items-start">
        <HotelFilterSidebar />

        <div className="overflow-hidden flex-1">
          <div className="md:mt-4">
            <div className="relative lg:ml-4 mr-1">
              <div className="h-0 " />
              {isLoading || isFetching ? (
                <div className="mt-4 space-y-4 pr-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i}>
                      <HotelCardLoadingSkeleton />
                    </div>
                  ))}
                </div>
              ) : result.length === 0 ? (
                <div className="rounded-xl border bg-background p-8 text-center">
                  <h3 className="text-base font-semibold">No hotels found</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Try adjusting filters or search with another
                    destination/date.
                  </p>
                </div>
              ) : (
                <div
                  className=""
                  ref={resultsRootRef}
                >
                  <Virtuoso
                    useWindowScroll
                    data={result}
                    overscan={200}
                    rangeChanged={handleRangeChange}
                    itemContent={renderHotelItem}
                    components={virtuosoComponents}
                  />
                </div>
              )}
              {showBackToTop && (
                <button
                  type="button"
                  aria-label="Back to top"
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  className="fixed bottom-6 right-6 z-40 rounded-full bg-primary text-primary-foreground shadow-lg p-2 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <ArrowUp className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
